import React, { useEffect, useState } from 'react';
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  SortingState,
  getSortedRowModel,
  useReactTable,
  InitialTableState,
  Row,
  RowSelectionState,
  HeaderContext,
} from '@tanstack/react-table';
import { useMediaQuery } from 'react-responsive';
import { Link } from 'react-router-dom';
import { Table, TableBody, TableCell, TableFooter, TableHead, TableHeader, TableRow } from '../Table';
import DataTablePagination, { IPaginationVariants } from './DataTablePagination';
import { cn } from '../../../../utils/styles';
import { Collapsible, CollapsibleContent } from '../Collapsible';
import Icons from '../../Icons/Icons';
import { Button } from '../Button';
import styles from './index.module.scss';

export type ExpandedRowRender<ValueType> = (record: ValueType) => React.ReactNode;

export interface ExpandableConfig<TData> {
  expandedRowRender: ExpandedRowRender<TData>;
  expandOnAction: 'row' | 'button';
  columnTitle?: React.ReactNode;
  // expandedRowKeys?: readonly Key[];
  // onExpand?: (expanded: boolean, record: TData) => void;
  // onExpandedRowsChange?: (expandedKeys: readonly Key[]) => void;
}

export interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  initialState?: InitialTableState;
  onRow?: (row: Row<TData>) => void;
  expandable?: ExpandableConfig<TData>;
  paginationPosition?: 'top' | 'bottom' | 'none';
  paginationVariant?: IPaginationVariants;
  withColumnsFooter?: boolean;
  className?: string;
  selectedRows?: RowSelectionState;
  variant?: 'default' | 'subTable' | 'bordered' | 'guidance' | 'guidance2' | 'subTable2';
  responsive?: boolean;
  minWidth?: number;
  emptyText?: string;
}

export default function DataTable<TData, TValue>({
  columns,
  data,
  initialState,
  onRow,
  paginationPosition,
  paginationVariant,
  withColumnsFooter,
  expandable,
  className,
  variant,
  selectedRows,
  responsive,
  emptyText,
  minWidth,
}: DataTableProps<TData, TValue>) {
  const isDesktop = useMediaQuery({ minWidth });
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});
  const [sorting, setSorting] = React.useState<SortingState>([]);

  const toggleRowExpansion = (rowId: string) => {
    setExpandedRows(prevExpandedRows => ({
      ...prevExpandedRows,
      [rowId]: !prevExpandedRows[rowId],
    }));
  };

  const [rowSelection, setRowSelection] = React.useState(selectedRows || {});

  const table = useReactTable({
    data,
    columns,
    initialState,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    onRowSelectionChange: setRowSelection,
    state: {
      sorting,
      rowSelection,
    },
  });

  useEffect(() => {
    setRowSelection(selectedRows || {});
  }, [selectedRows]);

  const getExpandColSpan = (): number => {
    if (!expandable) return 0;
    const { expandOnAction } = expandable;

    if (expandOnAction === 'button') {
      // Add button column to count.
      return (table.getVisibleLeafColumns()?.length || 0) + 1;
    }

    // columns.length;
    return table.getVisibleLeafColumns().length;
  };

  const [openRowIndex, setOpenRowIndex] = useState<number>();

  const handleOpenRow = (newRowIndex: number) => {
    setOpenRowIndex(newRowIndex === openRowIndex ? undefined : newRowIndex);
  };

  return (
    <div className={cn('table-with-pagination', className)}>
      {paginationPosition === 'top' && data.length > 4 ? (
        <DataTablePagination table={table} initialPageSize={initialState?.pagination?.pageSize} variant="extended" />
      ) : null}

      {!isDesktop && responsive ? (
        /** Mobile view */
        <div className="rounded-md child:mb-2 last:child:mb-0">
          {table.getRowModel().rows.map((row, rowIndex) => (
            <div
              key={row.id}
              className={cn('flex flex-col gap-3 relative w-full border rounded-lg py-2 px-3', {
                'cursor-pointer hover:bg-slate-200': onRow,
              })}
              // If this item have onRow then add additional props to component
              {...(onRow ? { onClick: () => onRow(row), tabIndex: -1, role: 'button' } : {})}
            >
              {row.getVisibleCells().some(el => el.column.columnDef.meta?.mobileHidden) ? (
                <div className="self-end">
                  <Link
                    to="#"
                    className="h-[30px] flex items-center justify-center w-[30px]"
                    onClick={e => {
                      e.preventDefault();
                      handleOpenRow(rowIndex);
                    }}
                  >
                    {rowIndex === openRowIndex ? <Icons.PlusFilled className="rotate-45" /> : <Icons.More />}
                  </Link>
                </div>
              ) : null}
              {row.getVisibleCells().map(cell => (
                <div
                  key={cell.column.id}
                  className={cn('flex justify-between gap-1 items-center', {
                    hidden: cell.column.columnDef.meta?.mobileHidden && rowIndex !== openRowIndex,
                    'flex-col items-start gap-[10px]': rowIndex === openRowIndex,
                  })}
                >
                  {/** If its action row then render without title */}
                  <div className="uppercase text-xs tracking-[0.1px] font-medium text-muted-foreground">
                    {cell.column.columnDef.id === 'actions'
                      ? ''
                      : flexRender(cell.column.columnDef.header, {} as HeaderContext<TData, any>)}
                  </div>
                  <div key={cell.id} className="text-base">
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      ) : (
        /** Desktop view */
        <div className="rounded-md">
          <Table rounded={className?.includes('rounded')} className={variant === 'guidance' ? styles.table : ''}>
            <TableHeader>
              {table.getHeaderGroups().map(headerGroup => (
                <TableRow
                  key={headerGroup.id}
                  className={cn('border-none', {
                    'bg-slate-100': variant === 'default' || variant === 'bordered',
                  })}
                >
                  {expandable && expandable.expandOnAction === 'button' ? (
                    <TableHead style={{ width: '52px' }}>{expandable.columnTitle}</TableHead>
                  ) : null}

                  {headerGroup.headers.map(header => (
                    <TableHead
                      key={header.id}
                      className={cn('uppercase text-xs tracking-[0.1px]', {
                        border: variant === 'bordered',
                      })}
                      colSpan={header.colSpan}
                      // Default column size is 150, so if size in config not default - apply it, in other case 'auto'
                      style={{
                        width: header.getSize() !== 150 ? `calc(${header.getSize()} * 1px)` : 'auto',
                      }}
                    >
                      {/* eslint-disable-next-line no-nested-ternary */}
                      {typeof header.column.columnDef.header === 'string' && header.column.columnDef.enableSorting ? (
                        /** If column have sorting enabled then render content inside of the sort button */
                        <Button
                          variant="ghost"
                          size="auto"
                          onClick={() => header.column.toggleSorting(header.column.getIsSorted() === 'asc')}
                          className="uppercase text-xs"
                        >
                          {header.isPlaceholder
                            ? null
                            : flexRender(header.column.columnDef.header, header.getContext())}
                          <Icons.Sorting className="h-5 w-5" />
                        </Button>
                      ) : header.isPlaceholder ? null : (
                        flexRender(header.column.columnDef.header, header.getContext())
                      )}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows?.length ? (
                table.getRowModel().rows.map((row, rowIndex) => (
                  // asChild Collapsible will render as TableRow but with functionality of Collapsible.
                  <Collapsible key={row.id} asChild open={expandedRows?.[row.id] === true}>
                    <>
                      <TableRow
                        key={row.id}
                        data-state={row.getIsSelected() && variant !== 'guidance2' && 'selected'}
                        /** On Tr and Td 'height' works as 'min-height', and row will grow with content */
                        className={cn(
                          'h-14 text-base border-none relative',
                          {
                            'cursor-pointer hover:bg-slate-200': onRow,
                            'hover:bg-addition1-3 [&_td_a]:hover:bg-primary [&_td_a]:hover:text-white':
                              (variant === 'guidance' && onRow) || (variant === 'guidance2' && onRow),
                            'bg-slate-100': rowIndex % 2 === 1 && (variant === 'default' || variant === 'bordered'),
                            'h-12': variant === 'subTable',
                            'h-12 bg-slate-100 font-medium': variant === 'subTable2',
                            'border-b border-muted border-solid ': expandedRows?.[row.id] === true,
                          },
                          variant === 'guidance' ? styles.tableRow : '',
                          variant === 'guidance2' && rowIndex % 2 === 0 ? styles.tableRow2 : '',
                        )}
                        onClick={() => {
                          if (onRow) {
                            onRow(row);
                          }
                          if (expandable && expandable.expandOnAction === 'row') {
                            toggleRowExpansion(row.id);
                          }
                        }}
                      >
                        {expandable && expandable.expandOnAction === 'button' ? (
                          <TableCell>
                            <Button
                              size="icon"
                              variant="outline"
                              onClick={() => toggleRowExpansion(row.id)}
                              className={cn({
                                'bg-transparent w-[30px] h-[30px]': variant === 'subTable2',
                              })}
                            >
                              <Icons.PlusMinusAnimated
                                active={!expandedRows?.[row.id]}
                                className={cn('text-primary', {
                                  'w-[13px] h-[13px]': variant === 'subTable2',
                                })}
                              />
                            </Button>
                          </TableCell>
                        ) : null}

                        {/* style={{ width: `${cell.column.getSize()}` }} */}
                        {row.getVisibleCells().map(cell => (
                          <TableCell key={cell.id} className={cn('text-addition1', { border: variant === 'bordered' })}>
                            <div className="relative">{flexRender(cell.column.columnDef.cell, cell.getContext())}</div>
                          </TableCell>
                        ))}
                      </TableRow>
                      {/** If Collapse is open then render content in Cell with width of all columns of table */}
                      <CollapsibleContent asChild>
                        <TableRow>
                          <TableCell colSpan={getExpandColSpan()}>
                            {expandable?.expandedRowRender?.(row.original)}
                          </TableCell>
                        </TableRow>
                      </CollapsibleContent>
                    </>
                  </Collapsible>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={columns.length} className="h-24 text-center text-primary">
                    {emptyText}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>

            {withColumnsFooter ? (
              <TableFooter>
                {table.getFooterGroups().map(footerGroup => (
                  <TableRow key={footerGroup.id} className="footer-row">
                    {footerGroup.headers.map(header => (
                      <TableHead key={header.id} colSpan={header.colSpan} className="footer-cell">
                        {header.isPlaceholder ? null : (
                          <div>{flexRender(header.column.columnDef.footer, header.getContext())}</div>
                        )}
                      </TableHead>
                    ))}
                  </TableRow>
                ))}
              </TableFooter>
            ) : null}
          </Table>
        </div>
      )}

      {paginationPosition === 'bottom' && data.length > 3 ? (
        <DataTablePagination
          table={table}
          initialPageSize={initialState?.pagination?.pageSize}
          variant={paginationVariant}
        />
      ) : null}
    </div>
  );
}

DataTable.defaultProps = {
  initialState: { pagination: { pageSize: 10 } },
  expandable: undefined,
  onRow: undefined,
  paginationPosition: 'top',
  paginationVariant: 'extended',
  withColumnsFooter: false,
  className: undefined,
  selectedRows: undefined,
  variant: 'default',
  responsive: false,
  minWidth: 768,
  emptyText: 'No Result.',
};
